/**
 * MentionPopup — dropdown for @-mention entity search.
 *
 * Two placement strategies:
 *   - anchor-bottom: absolute-positioned above its parent (agent composer,
 *     command bar). Default.
 *   - caret-rect: portaled to document.body with fixed positioning anchored
 *     to a live caret rect (notes editor).
 */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, JSX } from "react";
import { Icon } from "../ui/Icon";
import type { EntitySearchResult } from "../../modules/episodes/types";
import { schemaVisual, schemaLabel, allSchemaEntries } from "../../modules/shared/schemaIcons";
import { AVATAR_COLOR_CLASSES, type AvatarColor } from "../../modules/_base/sharedTypes";
import { normalizeSchemaId } from "../shared/entityHref";

// ─── Props ───────────────────────────────────────────────────────────

export type MentionPopupPlacement =
  | { readonly mode: "anchor-bottom" }
  | { readonly mode: "caret-rect"; readonly rect: DOMRect };

export interface MentionPopupProps {
  readonly results: readonly EntitySearchResult[];
  readonly isLoading: boolean;
  readonly selectedIndex: number;
  readonly hasQuery: boolean;
  readonly activeCategory: string | null;
  readonly onSelect: (item: EntitySearchResult) => void;
  readonly onCategorySelect: (schemaId: string) => void;
  readonly onCategoryBack: () => void;
  /** Defaults to `{ mode: "anchor-bottom" }` for back-compat with existing call sites. */
  readonly placement?: MentionPopupPlacement;
}

const POPUP_BASE_CLASS =
  "z-50 max-h-[320px] overflow-y-auto rounded-lg border border-edge bg-surface-secondary shadow-xl";

// ─── Component ───────────────────────────────────────────────────────

export function MentionPopup(props: MentionPopupProps): JSX.Element {
  const placement: MentionPopupPlacement = props.placement ?? { mode: "anchor-bottom" };

  if (placement.mode === "caret-rect") {
    return createPortal(
      <MentionPopupBody
        {...props}
        wrapperClass={POPUP_BASE_CLASS}
        wrapperStyle={caretRectStyle(placement.rect)}
        wrapperDataPlacement="caret-rect"
      />,
      document.body,
    );
  }

  return (
    <MentionPopupBody
      {...props}
      wrapperClass={`absolute bottom-full left-0 right-0 mb-1 ${POPUP_BASE_CLASS}`}
      wrapperDataPlacement="anchor-bottom"
    />
  );
}

function caretRectStyle(rect: DOMRect): CSSProperties {
  return {
    position: "fixed",
    left: rect.left,
    top: rect.bottom + 4,
    minWidth: 280,
    maxWidth: 360,
  };
}

interface BodyProps extends MentionPopupProps {
  readonly wrapperClass: string;
  readonly wrapperStyle?: CSSProperties;
  readonly wrapperDataPlacement: "anchor-bottom" | "caret-rect";
}

function MentionPopupBody({
  results,
  isLoading,
  selectedIndex,
  hasQuery,
  activeCategory,
  onSelect,
  onCategorySelect,
  onCategoryBack,
  wrapperClass,
  wrapperStyle,
  wrapperDataPlacement,
}: BodyProps): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = listRef.current;
    if (!container) return;
    const selected = container.querySelector("[data-selected='true']");
    if (selected) {
      selected.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const showCombo = !hasQuery && !activeCategory;

  return (
    <div
      ref={listRef}
      data-placement={wrapperDataPlacement}
      className={wrapperClass}
      style={wrapperStyle}
    >
      {showCombo ? (
        <ComboView
          results={results}
          isLoading={isLoading}
          selectedIndex={selectedIndex}
          onSelect={onSelect}
          onCategorySelect={onCategorySelect}
        />
      ) : (
        <SearchResultsView
          results={results}
          isLoading={isLoading}
          selectedIndex={selectedIndex}
          activeCategory={activeCategory}
          onSelect={onSelect}
          onCategorySelect={onCategorySelect}
          onCategoryBack={onCategoryBack}
        />
      )}
    </div>
  );
}

// ─── Combo view: recent + categories ─────────────────────────────────

function ComboView({
  results,
  isLoading,
  selectedIndex,
  onSelect,
  onCategorySelect,
}: {
  readonly results: readonly EntitySearchResult[];
  readonly isLoading: boolean;
  readonly selectedIndex: number;
  readonly onSelect: (item: EntitySearchResult) => void;
  readonly onCategorySelect: (schemaId: string) => void;
}): JSX.Element {
  const recentItems = results.slice(0, 4);

  return (
    <>
      {/* Recent entities */}
      {recentItems.length > 0 && (
        <div className="px-1.5 pt-1.5 pb-1">
          <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-agent-text-dim">
            Recent
          </div>
          {recentItems.map((item, i) => (
            <EntityRow
              key={item.id}
              item={item}
              isSelected={selectedIndex === i}
              onClick={() => { onSelect(item); }}
            />
          ))}
        </div>
      )}

      {isLoading && recentItems.length === 0 && (
        <div className="px-3 py-2 text-[12px] text-agent-text-dim">
          Loading...
        </div>
      )}

      {/* Category list */}
      <BrowseList
        selectedIndex={selectedIndex}
        startIndex={recentItems.length}
        onCategorySelect={onCategorySelect}
      />
    </>
  );
}

// ─── Shared browse-by-category list ──────────────────────────────────
//
// Rendered both in the empty-query combo view AND below search results so
// users can drill into a category WHILE typing — answers the "where's the
// menu?" feedback: the popup used to hide the category list the moment any
// query was present.
function BrowseList({
  selectedIndex,
  startIndex,
  onCategorySelect,
}: {
  readonly selectedIndex?: number | null;
  readonly startIndex?: number;
  readonly onCategorySelect: (schemaId: string) => void;
}): JSX.Element {
  return (
    <div
      data-testid="mention-browse"
      className="border-t border-edge px-1.5 pt-1 pb-1.5"
    >
      <div className="px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-agent-text-dim">
        Browse
      </div>
      {allSchemaEntries().map((cat, i) => {
        const idx = (startIndex ?? 0) + i;
        const isSelected = selectedIndex != null && selectedIndex === idx;
        return (
          <button
            key={cat.schemaId}
            data-selected={isSelected}
            className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] ${
              isSelected ? "bg-accent/20 text-white" : "text-agent-text hover:bg-white/5"
            }`}
            type="button"
            onClick={() => { onCategorySelect(cat.schemaId); }}
          >
            <Icon name={cat.icon} size={14} className="shrink-0 text-agent-text-muted" />
            <span className="flex-1">{cat.label}</span>
            <Icon name="chevron-right" size={12} className="text-agent-text-dim" />
          </button>
        );
      })}
    </div>
  );
}

// ─── Search results (flat list) ──────────────────────────────────────

function SearchResultsView({
  results,
  isLoading,
  selectedIndex,
  activeCategory,
  onSelect,
  onCategorySelect,
  onCategoryBack,
}: {
  readonly results: readonly EntitySearchResult[];
  readonly isLoading: boolean;
  readonly selectedIndex: number;
  readonly activeCategory: string | null;
  readonly onSelect: (item: EntitySearchResult) => void;
  readonly onCategorySelect: (schemaId: string) => void;
  readonly onCategoryBack: () => void;
}): JSX.Element {
  return (
    <>
      <div data-testid="mention-results" className="px-1.5 py-1.5">
        {activeCategory && (
          <button
            className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[12px] text-agent-text-muted hover:bg-white/5 mb-0.5"
            type="button"
            onClick={onCategoryBack}
          >
            <Icon name="chevron-left" size={12} />
            <span>{schemaLabel(activeCategory)}</span>
          </button>
        )}

        {results.length === 0 && !isLoading && (
          <div className="px-2 py-2 text-[12px] text-agent-text-dim">
            No results
          </div>
        )}

        {isLoading && results.length === 0 && (
          <div className="px-2 py-2 text-[12px] text-agent-text-dim">
            Searching...
          </div>
        )}

        {results.map((item, i) => (
          <EntityRow
            key={item.id}
            item={item}
            isSelected={selectedIndex === i}
            onClick={() => { onSelect(item); }}
          />
        ))}
      </div>

      {/* Browse-by-category list, hidden when the user has already drilled
          into a single category (the "back" button covers that case). */}
      {!activeCategory && <BrowseList onCategorySelect={onCategorySelect} />}
    </>
  );
}

// ─── Single entity row ───────────────────────────────────────────────

function EntityRow({
  item,
  isSelected,
  onClick,
}: {
  readonly item: EntitySearchResult;
  readonly isSelected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const visual = schemaVisual(normalizeSchemaId(item.schema_id));
  const iconColor = visual.themeColor
    ? AVATAR_COLOR_CLASSES[visual.themeColor as AvatarColor]
    : "text-agent-text-muted";

  return (
    <button
      data-selected={isSelected}
      className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] ${
        isSelected
          ? "bg-accent/20 text-white"
          : "text-agent-text hover:bg-white/5"
      }`}
      type="button"
      onClick={onClick}
    >
      <Icon
        name={visual.icon}
        size={14}
        className={`shrink-0 ${iconColor}`}
      />
      <span className="flex-1 truncate">{item.name ?? "Unnamed"}</span>
      <span className="shrink-0 text-[11px] text-agent-text-dim">
        {schemaLabel(normalizeSchemaId(item.schema_id))}
      </span>
    </button>
  );
}

/** Total number of selectable items in combo view (recent items + categories). */
// eslint-disable-next-line react-refresh/only-export-components
export function comboItemCount(recentCount: number): number {
  return Math.min(recentCount, 4) + allSchemaEntries().length;
}

/** Number of entity categories available. */
// eslint-disable-next-line react-refresh/only-export-components
export function categoryCount(): number {
  return allSchemaEntries().length;
}
